import type {
  SongHistoryDetailResponse,
  SongHistoryTimelineEvent,
} from "@/features/tracks/tracksApi";

export type TimelineDetailLine = { label: string; value: string };

export type TimelineActorDisplay = {
  label: string;
  /** True when this is a person or friendly system actor (not a raw ARN/id). */
  friendly: boolean;
};

const SYSTEM_ACTOR_LABEL = "KrossKast System";

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function str(value: unknown): string | null {
  if (value == null || value === "") return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return null;
}

function titleCaseStatus(status: unknown): string | null {
  const raw = str(status);
  if (!raw) return null;
  const s = raw.toLowerCase();
  if (s === "active") return "Active";
  if (s === "archived" || s === "deleted") {
    // Track soft-archive historically used status=deleted on some entities;
    // Song History timeline uses archived / deleted explicitly.
    if (s === "archived") return "Archived";
    return "Deleted";
  }
  if (s === "playable") return "Playable";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function purposeSourceLabel(purpose: unknown): string | null {
  const p = (str(purpose) || "").trim();
  if (!p || p === "initial") return "Original Upload";
  if (p === "reprocess") return "Audio Reprocessed";
  if (p === "replace-song-audio") return "Song Audio Replaced";
  return p;
}

function versionLabel(
  event: SongHistoryTimelineEvent,
  after: Record<string, unknown> | null,
): string | null {
  if (event.playbackVersionNumber != null) {
    return `v${event.playbackVersionNumber}`;
  }
  const fromAfter =
    after &&
    (after.playbackVersionNumber ?? after.version ?? after.versionsDisplay);
  if (fromAfter != null && fromAfter !== "") {
    const n = String(fromAfter);
    return n.startsWith("v") ? n : `v${n}`;
  }
  return null;
}

/** True when actorSub looks like an AWS IAM / STS principal (pipeline Lambda). */
export function isSystemActorSub(actorSub?: string | null): boolean {
  if (!actorSub) return false;
  const s = actorSub.trim();
  return (
    s.startsWith("arn:aws:") ||
    s.includes(":assumed-role/") ||
    s.includes("lambdaRole")
  );
}

/**
 * Resolve timeline actor for administrator-facing display.
 * Pipeline / IAM ARNs become “KrossKast System”; Auth0 subjects use the
 * employee directory resolver when provided.
 */
export function formatTimelineActor(
  actorSub: string | null | undefined,
  actorEmployeeId: string | null | undefined,
  resolvePerson: (
    sub?: string | null,
    employeeId?: string | null,
  ) => { label: string; resolved: boolean },
): TimelineActorDisplay | null {
  if (!actorSub && !actorEmployeeId) return null;
  if (isSystemActorSub(actorSub)) {
    return { label: SYSTEM_ACTOR_LABEL, friendly: true };
  }
  const person = resolvePerson(actorSub, actorEmployeeId);
  return {
    label: person.label,
    friendly: person.resolved,
  };
}

type SummaryContext = SongHistoryDetailResponse["summary"];

/**
 * Build human-readable detail lines for a timeline event.
 * Uses summary names when frozen before/after only carry entity IDs.
 */
export function formatTimelineDetailLines(
  event: SongHistoryTimelineEvent,
  summary: SummaryContext,
  actorLabel?: string | null,
): TimelineDetailLine[] {
  const before = asRecord(event.before);
  const after = asRecord(event.after);
  const lines: TimelineDetailLine[] = [];
  const push = (label: string, value: string | null | undefined) => {
    if (value == null || value === "") return;
    lines.push({ label, value });
  };

  switch (event.type) {
    case "song_created": {
      push(
        "Status",
        titleCaseStatus(after?.status) ||
          (summary.status === "deleted" || summary.status === "archived"
            ? "Active"
            : titleCaseStatus(summary.status)) ||
          "Active",
      );
      push("Title", str(after?.title) || summary.title || null);
      push("Artist", summary.artistName || null);
      push("Album", summary.albumTitle || null);
      push("Record Label", summary.recordLabelName || null);
      push(
        "Playback Version",
        versionLabel(event, after) ||
          summary.versionsDisplay ||
          (summary.playbackVersionNumber != null
            ? `v${summary.playbackVersionNumber}`
            : null),
      );
      break;
    }
    case "playback_updated": {
      const prev =
        before == null
          ? "None"
          : versionLabel(
              { ...event, playbackVersionNumber: null },
              before,
            ) ||
            str(before.artifactVersionId) ||
            "Previous version";
      push("Previous Version", prev);
      push(
        "New Version",
        versionLabel(event, after) ||
          str(after?.versionsDisplay) ||
          "—",
      );
      push(
        "Source",
        purposeSourceLabel(after?.sourceJobPurpose) ||
          purposeSourceLabel(event.source),
      );
      break;
    }
    case "archived": {
      push(
        "Previous State",
        titleCaseStatus(before?.status) || "Active",
      );
      push("New State", titleCaseStatus(after?.status) || "Archived");
      push("Archived By", actorLabel || null);
      push("Reason", event.reason || null);
      break;
    }
    case "restored": {
      push(
        "Previous State",
        titleCaseStatus(before?.status) || "Archived",
      );
      push("New State", titleCaseStatus(after?.status) || "Active");
      push("Restored By", actorLabel || null);
      push("Reason", event.reason || null);
      break;
    }
    case "permanently_deleted": {
      push(
        "Previous State",
        titleCaseStatus(before?.status) ||
          (summary.archivedAt ? "Archived" : "Active"),
      );
      push("New State", titleCaseStatus(after?.status) || "Deleted");
      push("Deleted By", actorLabel || null);
      push(
        "Reason",
        event.reason || summary.permanentDeleteReason || null,
      );
      push(
        "Deletion Manifest",
        str(after?.deletionManifestId) ||
          summary.deletionManifestId ||
          null,
      );
      break;
    }
    case "metadata_corrected": {
      const titleBefore = str(before?.title);
      const titleAfter = str(after?.title);
      if (titleBefore || titleAfter) {
        push("Previous Title", titleBefore || "—");
        push("New Title", titleAfter || "—");
      }
      push("Updated By", actorLabel || null);
      push("Reason", event.reason || null);
      break;
    }
    case "not_playable": {
      push("Previous State", titleCaseStatus(before?.status) || null);
      push("New State", "Not Playable");
      push("Updated By", actorLabel || null);
      push("Reason", event.reason || null);
      break;
    }
    default: {
      // Processing / upload events usually have no before/after payload.
      if (event.reason) push("Reason", event.reason);
      const statusAfter = titleCaseStatus(after?.status);
      const statusBefore = titleCaseStatus(before?.status);
      if (statusBefore || statusAfter) {
        if (statusBefore) push("Previous State", statusBefore);
        if (statusAfter) push("New State", statusAfter);
      }
      break;
    }
  }

  return lines;
}

export function timelineHasTechnicalPayload(
  event: SongHistoryTimelineEvent,
): boolean {
  return event.before != null || event.after != null;
}
